/**
 * Property Carousel block — editor component.
 *
 * @package
 * @since   3.5.0
 */

import { __ } from '@wordpress/i18n';
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';
import {
	PanelBody,
	RangeControl,
	SelectControl,
	ToggleControl,
} from '@wordpress/components';

import BlockPreview from '../shared/BlockPreview';
import SectionHeaderPanel from '../shared/SectionHeaderPanel';

const ORDERBY_OPTIONS = [
	{ label: __( 'Newest first', 'havenlytics' ), value: 'date' },
	{ label: __( 'Title (A–Z)', 'havenlytics' ), value: 'title' },
	{ label: __( 'Price: low to high', 'havenlytics' ), value: 'price_low' },
	{ label: __( 'Price: high to low', 'havenlytics' ), value: 'price_high' },
	{ label: __( 'Random', 'havenlytics' ), value: 'rand' },
];

export default function Edit( { attributes, setAttributes } ) {
	const {
		postsPerPage,
		visibleSlides,
		visibleTablet,
		visibleMobile,
		orderby,
		featuredOnly,
		centerMode,
		autoplay,
		showNav,
		showDots,
	} = attributes;
	const blockProps = useBlockProps();

	return (
		<>
			<InspectorControls>
				<SectionHeaderPanel
					attributes={ attributes }
					setAttributes={ setAttributes }
				/>

				<PanelBody
					title={ __( 'Carousel', 'havenlytics' ) }
					initialOpen={ true }
				>
					<p className="components-base-control__help">
						{ __(
							'Slide count and navigation for the property carousel.',
							'havenlytics'
						) }
					</p>
					<ToggleControl
						label={ __( 'Center mode', 'havenlytics' ) }
						checked={ !! centerMode }
						onChange={ ( value ) =>
							setAttributes( { centerMode: value } )
						}
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Autoplay', 'havenlytics' ) }
						checked={ !! autoplay }
						onChange={ ( value ) =>
							setAttributes( { autoplay: value } )
						}
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show arrows', 'havenlytics' ) }
						checked={ !! showNav }
						onChange={ ( value ) =>
							setAttributes( { showNav: value } )
						}
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show dots', 'havenlytics' ) }
						checked={ !! showDots }
						onChange={ ( value ) =>
							setAttributes( { showDots: value } )
						}
						__nextHasNoMarginBottom
					/>
				</PanelBody>

				<PanelBody
					title={ __( 'Responsive slides', 'havenlytics' ) }
					initialOpen={ false }
				>
					<RangeControl
						label={ __( 'Desktop', 'havenlytics' ) }
						value={ visibleSlides }
						min={ 1 }
						max={ 5 }
						onChange={ ( value ) =>
							setAttributes( { visibleSlides: value } )
						}
						__nextHasNoMarginBottom
					/>
					<RangeControl
						label={ __( 'Tablet', 'havenlytics' ) }
						value={ visibleTablet }
						min={ 1 }
						max={ 4 }
						onChange={ ( value ) =>
							setAttributes( { visibleTablet: value } )
						}
						__nextHasNoMarginBottom
					/>
					<RangeControl
						label={ __( 'Mobile', 'havenlytics' ) }
						value={ visibleMobile }
						min={ 1 }
						max={ 2 }
						onChange={ ( value ) =>
							setAttributes( { visibleMobile: value } )
						}
						__nextHasNoMarginBottom
					/>
				</PanelBody>

				<PanelBody
					title={ __( 'Query', 'havenlytics' ) }
					initialOpen={ false }
				>
					<RangeControl
						label={ __( 'Number of properties', 'havenlytics' ) }
						value={ postsPerPage }
						min={ 1 }
						max={ 24 }
						onChange={ ( value ) =>
							setAttributes( { postsPerPage: value } )
						}
						__nextHasNoMarginBottom
					/>
					<SelectControl
						label={ __( 'Order by', 'havenlytics' ) }
						value={ orderby }
						options={ ORDERBY_OPTIONS }
						onChange={ ( value ) =>
							setAttributes( { orderby: value } )
						}
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Featured only', 'havenlytics' ) }
						checked={ !! featuredOnly }
						onChange={ ( value ) =>
							setAttributes( { featuredOnly: value } )
						}
						__nextHasNoMarginBottom
					/>
				</PanelBody>
			</InspectorControls>

			<div { ...blockProps }>
				<BlockPreview
					block="havenlytics/property-carousel"
					attributes={ attributes }
				/>
			</div>
		</>
	);
}
